/**
 * FloView 범례 — model.legend 에 실제로 쓰인 키만 고정 순서로, 경고(model.warnings) 는 뒤에 덧붙인다.
 * 견본 도형은 노드·엣지 그림과 같은 색·모양을 작게 줄여 그린다.
 * 중립 leaf — pages/** 를 import 하지 않는다.
 */
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { common, grey } from "@mui/material/colors";

import { FS } from "@plannel/utils/font-util";
import { FLO_COLORS, EDGE_COLOR } from "@plannel/components/flo/floTokens";

// 견본 필 안 글자 크기(px) — 18px 고정 견본 안에 맞춘 값이라 글꼴 배율을 따르지 않는다.
const SAMPLE_GLYPH_PX = 11;

// 견본 SVG 높이(px) — 도형은 세로 가운데에 두고, 테두리까지 이 안에 들어가게 잡아 아래가 잘리지 않는다.
const SAMPLE_H = 18;
const SAMPLE_MID = SAMPLE_H / 2;

// 가로로 긴 견본(공정·설비·칩·필) 폭과 짧은 견본(삼각형·선) 폭.
const WIDE_W = 24;
const NARROW_W = 17;

// 범례 키 -> 견본 도형. model.legend 가 이미 결정형 순서로 쓰인 것만 담아 준다.
const SAMPLE_BY_KEY = {
  stock: "triFill", purchased: "triOutline", root: "triRoot", make: "boxMake", kit: "boxKit",
  transport: "boxDashed", resource: "ellipse", resourceMore: "resMore",
  materialFlow: "lineSolid", transferFlow: "lineDashed", qty: "chip", ratio: "chipRatio",
  more: "more", folded: "triAgg",
};

/** camelCase 키 -> i18n 키의 PascalCase 접미사. */
function pascalOf(key) {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function LegendSample({ kind, family }) {
  const svg = (w, body) => (
    <svg width={w} height={SAMPLE_H} style={{ display: "block", flexShrink: 0 }}>{body}</svg>
  );
  const tri = (fill, stroke) => svg(NARROW_W,
    <polygon points="8.5,3 15,15 2,15" fill={fill} stroke={stroke} strokeWidth={1.5} />);
  const box = (fill, stroke, dash) => svg(WIDE_W,
    <rect x="2.5" y="3.5" width="19" height="11" rx="4" fill={fill} stroke={stroke} strokeWidth={1.5}
          strokeDasharray={dash} />);
  const line = (stroke, dash) => svg(NARROW_W,
    <line x1="0" y1={SAMPLE_MID} x2={NARROW_W} y2={SAMPLE_MID} stroke={stroke} strokeWidth={2} strokeDasharray={dash} />);
  // 1px 테두리 칩은 반 픽셀 좌표에 두어 선이 번지지 않게 한다.
  const chip = (fill, stroke) => svg(WIDE_W,
    <rect x="1.5" y="2.5" width="21" height="13" rx="6.5" fill={fill} stroke={stroke} />);
  const pill = (fill, stroke, text) => svg(WIDE_W,
    <g>
      <rect x="1.5" y="1.5" width="21" height="15" rx="7.5" fill={fill} stroke={stroke} strokeWidth={1.5} />
      <text x={WIDE_W / 2} y="13" textAnchor="middle"
            style={{ font: `700 ${SAMPLE_GLYPH_PX}px ${family}`, fill: common.black }}>{text}</text>
    </g>);
  switch (kind) {
    case "triFill": return tri(FLO_COLORS.invFill, FLO_COLORS.invStroke);
    case "triOutline": return tri(FLO_COLORS.invPurFill, FLO_COLORS.invPurStroke);
    case "triRoot": return tri(FLO_COLORS.invRootFill, FLO_COLORS.invRootStroke);
    case "triAgg": return tri(FLO_COLORS.invAggFill, FLO_COLORS.invAggStroke);
    case "boxMake": return box(FLO_COLORS.makeFill, FLO_COLORS.makeStroke);
    case "boxKit": return box(FLO_COLORS.kitFill, FLO_COLORS.kitStroke);
    case "boxDashed": return box(FLO_COLORS.moveFill, FLO_COLORS.moveStroke, "4 2");
    case "ellipse": return svg(WIDE_W,
      <ellipse cx={WIDE_W / 2} cy={SAMPLE_MID} rx="9.5" ry="5.5"
               fill={FLO_COLORS.resFill} stroke={FLO_COLORS.resStroke} strokeWidth={1.5} />);
    case "lineSolid": return line(EDGE_COLOR.make);
    case "lineDashed": return line(EDGE_COLOR.move, "5 3");
    case "chip": return chip(FLO_COLORS.chipQtyFill, FLO_COLORS.chipQtyBorder);
    case "chipRatio": return chip(FLO_COLORS.chipRatioFill, FLO_COLORS.chipRatioBorder);
    case "gap": return pill(FLO_COLORS.invGapFill, FLO_COLORS.invGapStroke, "!");
    case "more": return pill(FLO_COLORS.moreFill, FLO_COLORS.moreStroke, "…");
    case "resMore": return pill(common.white, grey[700], "+N");
    default: return null;
  }
}

const ITEM_SX = { display: "flex", alignItems: "center", gap: 0.5 };
const LABEL_SX = { fontSize: FS.BODY, color: "text.secondary", whiteSpace: "nowrap" };

export default function FloViewLegend({ model, fonts }) {
  const { t } = useTranslation();
  const family = fonts?.family;
  const legend = model?.legend || [];
  const warnings = model?.warnings || [];
  if (!legend.length && !warnings.length) return null;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25 }}>
      {legend.map((key) => (
        <Box key={key} sx={ITEM_SX}>
          <LegendSample kind={SAMPLE_BY_KEY[key]} family={family} />
          <Typography sx={LABEL_SX}>{t(`floViewLegend${pascalOf(key)}`)}</Typography>
        </Box>
      ))}
      {warnings.map((text, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={`${idx}-${text}`} sx={ITEM_SX}>
          <LegendSample kind="gap" family={family} />
          <Typography sx={LABEL_SX}>{text}</Typography>
        </Box>
      ))}
    </Box>
  );
}
